import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';

export interface MatchPeriod {
  id: string;
  name: string;
  startTimestamp: string | null;
  endTimestamp: string | null;
}

export interface MatchPeriods {
  firstHalf: MatchPeriod | null;
  secondHalf: MatchPeriod | null;
  extraTimeFirstHalf: MatchPeriod | null;
  extraTimeSecondHalf: MatchPeriod | null;
  penaltyShootout: MatchPeriod | null;
}

/**
 * Hook to fetch match periods from key_match_events table
 * Determines time boundaries for different match phases
 */
export const useMatchPeriods = (matchId: string | undefined) => {
  const [periods, setPeriods] = useState<MatchPeriods | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!matchId) {
      setLoading(false);
      return;
    }

    const fetchPeriods = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, get stat type IDs for game flow events
        const { data: statTypes, error: statTypesError } = await supabase
          .from('stat_types')
          .select('id, stat_key')
          .in('stat_key', [
            'kick_off',
            'end_1st_half',
            'start_2nd_half',
            'full_time',
            'start_extra_time_1st_half',
            'end_extra_time_1st_half',
            'start_extra_time_2nd_half',
            'penalty_shootout'
          ])
          .eq('is_active', true);

        if (statTypesError) {
          throw new Error(`Failed to fetch stat types: ${statTypesError.message}`);
        }

        const statTypeIds = statTypes?.map(st => st.id) || [];
        const statKeyMap = new Map(statTypes?.map(st => [st.id, st.stat_key]) || []);

        // Fetch key events for game flow stat types
        const { data: events, error: fetchError } = await supabase
          .from('key_match_events')
          .select(`
            id,
            stat_type_id,
            event_timestamp
          `)
          .eq('match_id', matchId)
          .eq('is_active', true)
          .in('stat_type_id', statTypeIds)
          .order('event_timestamp', { ascending: true });

        if (fetchError) {
          throw new Error(`Failed to fetch match periods: ${fetchError.message}`);
        }

        // If no events found, try match_timeline table as fallback
        if (!events || events.length === 0) {
          // Get stat type IDs for filtering
          const statTypeIds = statTypes?.map(st => st.id) || [];
          
          const { data: timelineEvents, error: timelineError } = await supabase
            .from('match_timeline')
            .select(`
              id,
              stat_type_id,
              event_timestamp
            `)
            .eq('match_id', matchId)
            .eq('is_active', true)
            .in('stat_type_id', statTypeIds)
            .order('event_timestamp', { ascending: true });

          if (timelineError) {
            console.warn('Failed to fetch from match_timeline:', timelineError);
          } else if (timelineEvents && timelineEvents.length > 0) {
            // Use the same statKeyMap from earlier
            // Use timeline events
            const kickOff = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'kick_off');
            const end1stHalf = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'end_1st_half');
            const start2ndHalf = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'start_2nd_half');
            const startExtraTime1stHalf = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'start_extra_time_1st_half');
            const endExtraTime1stHalf = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'end_extra_time_1st_half');
            const startExtraTime2ndHalf = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'start_extra_time_2nd_half');
            const penaltyShootout = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'penalty_shootout');
            const fullTime = timelineEvents.find((e: any) => statKeyMap.get(e.stat_type_id) === 'full_time');

            const matchPeriods: MatchPeriods = {
              firstHalf: kickOff && end1stHalf
                ? {
                    id: 'first_half',
                    name: 'First Half',
                    startTimestamp: kickOff.event_timestamp,
                    endTimestamp: end1stHalf.event_timestamp,
                  }
                : null,
              // Second half exists if start2ndHalf exists
              // End timestamp can be: endExtraTime1stHalf, fullTime, or null (if match is still ongoing)
              secondHalf: start2ndHalf
                ? {
                    id: 'second_half',
                    name: 'Second Half',
                    startTimestamp: start2ndHalf.event_timestamp,
                    endTimestamp: endExtraTime1stHalf?.event_timestamp || fullTime?.event_timestamp || null,
                  }
                : null,
              extraTimeFirstHalf: startExtraTime1stHalf && endExtraTime1stHalf
                ? {
                    id: 'extra_time_1st_half',
                    name: 'Extra Time 1st Half',
                    startTimestamp: startExtraTime1stHalf.event_timestamp,
                    endTimestamp: endExtraTime1stHalf.event_timestamp,
                  }
                : null,
              extraTimeSecondHalf: startExtraTime2ndHalf && (penaltyShootout || fullTime)
                ? {
                    id: 'extra_time_2nd_half',
                    name: 'Extra Time 2nd Half',
                    startTimestamp: startExtraTime2ndHalf.event_timestamp,
                    endTimestamp: penaltyShootout?.event_timestamp || fullTime?.event_timestamp || null,
                  }
                : null,
              penaltyShootout: penaltyShootout && fullTime
                ? {
                    id: 'penalty_shootout',
                    name: 'Penalty Shootout',
                    startTimestamp: penaltyShootout.event_timestamp,
                    endTimestamp: fullTime.event_timestamp,
                  }
                : null,
              // Don't include fullMatch - "All Periods" already covers this
            };

            setPeriods(matchPeriods);
            setLoading(false);
            return;
          }
        }

        // Process key_match_events - map stat_type_id to stat_key
        const kickOff = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'kick_off');
        const end1stHalf = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'end_1st_half');
        const start2ndHalf = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'start_2nd_half');
        const startExtraTime1stHalf = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'start_extra_time_1st_half');
        const endExtraTime1stHalf = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'end_extra_time_1st_half');
        const startExtraTime2ndHalf = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'start_extra_time_2nd_half');
        const penaltyShootout = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'penalty_shootout');
        const fullTime = events?.find((e: any) => statKeyMap.get(e.stat_type_id) === 'full_time');

        const matchPeriods: MatchPeriods = {
          firstHalf: kickOff && end1stHalf
            ? {
                id: 'first_half',
                name: 'First Half',
                startTimestamp: kickOff.event_timestamp,
                endTimestamp: end1stHalf.event_timestamp,
              }
            : null,
          // Second half exists if start2ndHalf exists
          // End timestamp can be: startExtraTime1stHalf, fullTime, or null (if match is still ongoing)
          secondHalf: start2ndHalf
            ? {
                id: 'second_half',
                name: 'Second Half',
                startTimestamp: start2ndHalf.event_timestamp,
                endTimestamp: startExtraTime1stHalf?.event_timestamp || fullTime?.event_timestamp || null,
              }
            : null,
          extraTimeFirstHalf: startExtraTime1stHalf && endExtraTime1stHalf
            ? {
                id: 'extra_time_1st_half',
                name: 'Extra Time 1st Half',
                startTimestamp: startExtraTime1stHalf.event_timestamp,
                endTimestamp: endExtraTime1stHalf.event_timestamp,
              }
            : null,
          extraTimeSecondHalf: startExtraTime2ndHalf && (penaltyShootout || fullTime)
            ? {
                id: 'extra_time_2nd_half',
                name: 'Extra Time 2nd Half',
                startTimestamp: startExtraTime2ndHalf.event_timestamp,
                endTimestamp: penaltyShootout?.event_timestamp || fullTime?.event_timestamp || null,
              }
            : null,
          penaltyShootout: penaltyShootout && fullTime
            ? {
                id: 'penalty_shootout',
                name: 'Penalty Shootout',
                startTimestamp: penaltyShootout.event_timestamp,
                endTimestamp: fullTime.event_timestamp,
              }
            : null,
          // Don't include fullMatch - "All Periods" already covers this
        };

        setPeriods(matchPeriods);
      } catch (err) {
        console.error('Error fetching match periods:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch match periods');
      } finally {
        setLoading(false);
      }
    };

    fetchPeriods();
  }, [matchId]);

  // Get available periods (only those that exist)
  const availablePeriods = useMemo(() => {
    if (!periods) return [];
    const periodList: MatchPeriod[] = [];
    if (periods.firstHalf) periodList.push(periods.firstHalf);
    if (periods.secondHalf) periodList.push(periods.secondHalf);
    if (periods.extraTimeFirstHalf) periodList.push(periods.extraTimeFirstHalf);
    if (periods.extraTimeSecondHalf) periodList.push(periods.extraTimeSecondHalf);
    if (periods.penaltyShootout) periodList.push(periods.penaltyShootout);
    return periodList;
  }, [periods]);

  return {
    periods,
    availablePeriods,
    loading,
    error,
  };
};

